import { Component, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { TranslateService } from '@ngx-translate/core';
import { interval, lastValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { AlertModalComponent } from '../components/alert-modal/alert-modal.component';
import { CheckRolesService } from '../services/checkRoles/checkRoles.service';
import { CornerstoneService } from '../services/cornerstone/cornerstone.service';
import { SharedService } from '../services/shared/shared.service';
import { UserService } from '../services/user/user.service';
import { RedirectProperty, RedirectTarget } from '../types';
import { ProfileModalComponent } from './user/user-components/profile-modal/profile-modal.component';
import { isMaintenanceActive, SERVICE_NOW_MAINTENANCE } from './maintenance/maintenance.config';

@Component({
  selector: 'app-panel',
  templateUrl: './panel.page.html',
  styleUrls: ['./panel.page.scss'],
  standalone: false,
})
export class PanelPage {
  private readonly mobileTabBreakpoint = 445;
  private readonly benefitsRouterParam = 'benefits';

  constructor(
    public router: Router,
    private modalCtrl: ModalController,
    private userService: UserService,
    private cornerstoneService: CornerstoneService,
    private checkRolesService: CheckRolesService,
    private sharedService: SharedService,
    private translate: TranslateService,
    private storage: Storage,
  ) {
    interval(5000).subscribe(async () => {
      await this.checkTokenSession();
    });

    this.updateViewportFlags();
  }

  public appVersion = (environment.id !== 'PRD' ? environment.id + ' - ' : '') + environment.version;
  showApps = false;
  showReceipts = true;
  showUpdates = false;
  showSettings = false;
  showIaAgent = false;
  showPortalUnipac = false;
  showPSuggestion = false;
  showObservatory = false;
  showEmployeeUpdate = false;
  showServiceNow = false;
  showOnTime = false;
  showRecruitmentSelection = false;
  showTermManagement = false;
  showCornerstone = false;
  showBenefitsStatement = false;
  hideModuleUpdates: boolean = false;
  hideModuleReceipts: boolean = false;
  hideModuleWeb: boolean = false;
  routeUrl = this.router.url;
  // lineControl = false;
  payslip = true;
  hasEmail = true;
  showAppsTools = false;
  receipts = false;
  updates = false;
  showNormative = false;
  module: 'payments' | 'network' | 'updates' = 'network';
  isMenuExpanded: boolean = false;
  selectedUpdateIcon: string = '/assets/icon/account_circle.svg';
  currentUpdatePage: string = 'personProfile';
  targetLinks: RedirectTarget = environment.redirect;
  isMobileView = false;
  // thematicSchedule: boolean = false;

  get isServiceNowMaintenance(): boolean {
    return isMaintenanceActive(SERVICE_NOW_MAINTENANCE);
  }

  updatesApps = [
    {
      title: 'employeeUpdate.personProfile',
      routerLink: '/panel/hrservices/employee-update-person-profile',
      routerParams: 'personProfile',
      iconSrc: '/assets/icon/panel/person-icon.svg',
      iconNavSrc: '/assets/icon/account_circle.svg',
      hidden: false,
    },
    {
      title: 'employeeUpdate.address',
      routerLink: '/panel/hrservices/employee-update-person-profile',
      routerParams: 'address',
      iconSrc: '/assets/icon/panel/address.svg',
      iconNavSrc: 'assets/icon/location.svg',
      hidden: false,
    },
    {
      title: 'employeeUpdate.education',
      routerLink: '/panel/hrservices/employee-update-person-profile',
      routerParams: 'education',
      iconSrc: '/assets/icon/panel/education.svg',
      iconNavSrc: '/assets/icon/book.svg',
      hidden: false,
    },
    {
      title: 'employeeUpdate.curriculum',
      routerLink: '/panel/hrservices/employee-update-person-profile',
      routerParams: 'curriculum',
      iconSrc: '/assets/icon/panel/vaccinationCard.svg',
      iconNavSrc: '/assets/icon/article.svg',
      hidden: false,
    },
    {
      title: 'employeeUpdate.trainings',
      routerLink: '/panel/hrservices/employee-update-person-profile',
      routerParams: 'trainings',
      iconSrc: '/assets/icon/panel/diploma.svg',
      iconNavSrc: '/assets/icon/school.svg',
      hidden: false,
    },
    {
      title: 'employeeUpdate.members',
      routerLink: '/panel/hrservices/employee-update-members',
      routerParams: 'members',
      iconSrc: '/assets/icon/panel/members-icon.svg',
      iconNavSrc: '/assets/icon/family_restroom.svg',
      hidden: false,
    },
    {
      title: 'employeeUpdate.healthPlan',
      routerLink: '/panel/hrservices/employee-update-health-plan',
      routerParams: 'healthPlan',
      iconSrc: '/assets/icon/panel/health-icon.svg',
      iconNavSrc: '/assets/icon/ecg_heart.svg',
      hidden: false,
    },
    {
      title: 'employeeUpdate.lifeInsurance',
      routerLink: '/panel/hrservices/employee-update-life-insurance',
      routerParams: 'lifeInsurance',
      iconSrc: '/assets/icon/panel/assurance-icon.svg',
      iconNavSrc: '/assets/icon/health_and_safety.svg',
      hidden: false,
    },
    {
      title: 'employeeUpdate.updates',
      routerLink: '/panel/hrservices/employee-update-updates',
      routerParams: 'updates',
      iconSrc: '/assets/icon/panel/updates-icon.svg',
      iconNavSrc: '/assets/icon/notifications_active.svg',
      hidden: false,
    },
  ];
  receiptsApps = [
    {
      title: 'panel.app.statement.payslip',
      routerLink: '/panel/payments/payslip',
      routerParams: 'payslip',
      sidebarIconSrc: '/assets/icon/panel/statement/statement-payslip.svg',
      menuIconSrc: '/assets/icon/panel/statement/statement-payslip-icon.svg',
      hidden: false,
    },
    {
      title: 'panel.app.statement.vacation',
      routerLink: '/panel/payments/vacation',
      routerParams: 'vacation',
      sidebarIconSrc: '/assets/icon/panel/statement/statement-vacation.svg',
      menuIconSrc: '/assets/icon/panel/statement/statement-vacation-icon.svg',
      hidden: false,
    },
    {
      title: 'panel.app.statement.income',
      routerLink: '/panel/payments/income',
      routerParams: 'income',
      sidebarIconSrc: '/assets/icon/panel/statement/statement-income.svg',
      menuIconSrc: '/assets/icon/panel/statement/statement-income-icon.svg',
      hidden: false,
    },
    {
      title: 'panel.app.statement.benefits',
      routerLink: '/panel/payments/benefits',
      routerParams: 'benefits',
      sidebarIconSrc: '/assets/icon/panel/statement/statement-benefit.svg',
      menuIconSrc: '/assets/icon/panel/statement/statement-benefit-icon.svg',
      hidden: false,
    },
    {
      title: 'panel.app.statement.health',
      routerLink: '/panel/payments/health',
      routerParams: 'health',
      sidebarIconSrc: '/assets/icon/panel/statement/statement-health.svg',
      menuIconSrc: '/assets/icon/panel/statement/statement-health-icon.svg',
      hidden: false,
    },
    {
      title: 'panel.app.statement.dental',
      routerLink: '/panel/payments/dental',
      routerParams: 'dental',
      sidebarIconSrc: '/assets/icon/panel/statement/statement-dental.svg',
      menuIconSrc: '/assets/icon/panel/statement/statement-dental-icon.svg',
      hidden: false,
    },
    {
      title: 'panel.app.statement.timecard',
      routerLink: '/panel/payments/timecard',
      routerParams: 'timecard',
      sidebarIconSrc: '/assets/icon/panel/statement/statement-timecard.svg',
      menuIconSrc: '/assets/icon/panel/statement/statement-timecard-icon.svg',
      hidden: false,
    },
  ];

  get statementApps() {
    return this.receiptsApps.filter(app =>
      !app.hidden &&
      [
        'payslip',
        'vacation',
        'income',
        'benefits',
        'health',
        'dental',
        'timecard',
      ].includes(app.routerParams),
    );
  }

  get paymentTabApps() {
    if (!this.isMobileView) {
      return this.statementApps;
    }

    return this.receiptsApps.filter(
      app => !app.hidden && ['payslip', 'health', 'timecard'].includes(app.routerParams),
    );
  }

  @HostListener('window:resize')
  onResize() {
    this.updateViewportFlags();
  }

  private updateViewportFlags() {
    this.isMobileView = typeof window !== 'undefined' && window.innerWidth <= this.mobileTabBreakpoint;
  }

  toggleUpdatesMenu() {
    this.isMenuExpanded = !this.isMenuExpanded;
  }

  async selectMobileOption(pageParams: string) {
    const selectedApp = this.updatesApps.find(app => app.routerParams === pageParams);

    if (selectedApp) {
      await this.refreshAndChangeUpdatesModule(pageParams);

      if (selectedApp.routerLink) {
        await this.router.navigateByUrl(selectedApp.routerLink);
      }

      this.currentUpdatePage = pageParams;
      this.selectedUpdateIcon = selectedApp.iconNavSrc;

      await new Promise(resolve => setTimeout(resolve, 50));
      this.isMenuExpanded = false;
    }
  }

  async checkTokenSession() {
    let user = await this.userService.getUserAuth();
    const dateNow = new Date();
    const delta = Math.abs(dateNow.getTime() - new Date(user.lastLogin).getTime()) / 1000;
    const minutes = Math.floor(delta / 60) % 60;

    if (!user.token || !user.lastLogin || minutes > 20) {
      await this.userService.closeAllModals();
      this.userService.logout(true);
    }
  }

  async ionViewWillEnter() {
    this.hasEmail = await this.checkRolesService.checkEmployeeEmail();
    this.routeUrl = this.router.url;

    if (this.routeUrl.includes('employee')) {
      this.module = 'updates';
      this.updates = true;
      this.hideModuleUpdates = false;
      this.hideModuleWeb = true;
      this.showNormative = false;

      const currentApp = this.updatesApps.find(app => this.routeUrl.includes(app.routerParams));
      if (currentApp) {
        this.currentUpdatePage = currentApp.routerParams;
        this.selectedUpdateIcon = currentApp.iconNavSrc;
      }
    }

    if (this.routeUrl.includes('network') || this.routeUrl.includes('normative')) {
      if (this.routeUrl.includes('home')) {
        this.refreshAndChangeWebModule('home');
      }

      this.module = 'network';
      this.receipts = false;
      this.updates = false;
      this.hideModuleReceipts = true;
      this.hideModuleUpdates = true;
      this.hideModuleWeb = false;
      this.showNormative = this.routeUrl.includes('normative');
    }

    if (this.routeUrl.includes('payments')) {
      this.module = 'payments';
      this.receipts = true;
      this.hideModuleReceipts = false;
      this.hideModuleWeb = true;
      this.showNormative = false;
    }

    await this.checkPayments();
    await this.checkEmployeeUpdate();
    await this.checkBenefitsStatement();
    await this.checkAccess();

    if (!this.payslip) {
      this.showReceipts = false;
    }
  }

  async checkRouterLink() {
    if (this.payslip) {
      this.router.navigateByUrl('/panel/payments/payslip');
    } else if (!this.payslip) {
      this.router.navigateByUrl('/panel/payments/lineControl');
    } else {
      this.sharedService.showToast(this.translate.instant('common.notAllow'));
      this.hideModuleReceipts = true;
      this.hideModuleUpdates = true;
      this.hideModuleWeb = false;
      this.router.navigateByUrl('/panel/network/home');
    }
  }

  closeMenu() {
    setTimeout(() => {
      this.showApps = false;
    }, 500);
  }

  changeOverlay(type: string) {
    if (type === 'receipts') {
      this.receipts = !this.receipts;
      this.showApps = false;
    } else if (type === 'updates') {
      this.updates = !this.updates;
      this.showApps = false;
    } else if (type === 'apps') {
      this.showApps = !this.showApps;
      this.showSettings = false;
    } else if (type === 'normative') {
      this.showNormative = !this.showNormative;
      this.showApps = false;
    }
  }

  refreshInAnotherComponent() {
    this.sharedService.notifyOther({ refresh: true });
  }

  refreshAndChangeReceiptsModule(page?: string) {
    this.sharedService.notifyOther({ refresh: true, page: page });
    this.hideModuleReceipts = false;
    this.hideModuleWeb = true;
  }

  async refreshAndChangeUpdatesModule(page?: string) {
    let emailConfirmed: any;

    try {
      const res = await lastValueFrom(this.userService.getMe());
      emailConfirmed = res.emailConfirmed;
    } catch (error) {
      console.error('Erro ao validar usuário', error);
    }

    if (emailConfirmed) {
      const app = this.updatesApps.find(a => a.routerParams === page);
      if (app) {
        this.currentUpdatePage = app.routerParams;
        this.selectedUpdateIcon = app.iconNavSrc;
      }

      if (page === 'curriculum') {
        this.gotoCornerstone('/phnx/driver.aspx?routename=Social/UniversalProfile/Resume');
      } else if (page === 'trainings') {
        this.gotoCornerstone(
          '/phnx/driver.aspx?routename=Social/UniversalProfile/Transcript&TargetUser=1792&preSelectedCategoryId=3',
        );
      }

      this.sharedService.notifyOther({ refresh: true, page: page });

      this.hideModuleUpdates = false;
      this.hideModuleWeb = true;
      this.updates = true;
    } else {
      this.updates = false;
      this.openModalConfirmEmail();
      this.sharedService.showToast(this.translate.instant('employeeUpdate.messageConfirmEmail'));
    }
  }

  async refreshAndChangeWebModule(page: any) {
    await this.sharedService.notifyOther({ refresh: true, page: page });
    this.hideModuleWeb = false;
    this.hideModuleReceipts = true;
    this.hideModuleUpdates = true;
    this.showNormative = false;
  }

  async openModalConfirmEmail() {
    const title = 'Confirmação de e-mail';
    const typeAlert = 'confirmEmail';
    const modal = await this.modalCtrl.create({
      component: AlertModalComponent,
      componentProps: {
        title,
        typeAlert,
      },
      backdropDismiss: true,
      cssClass: 'j-medium-modal',
    });
    await modal.present();
  }

  async showModal() {
    let url = this.router.url;
    const modal = await this.modalCtrl.create({
      component: ProfileModalComponent,
      backdropDismiss: true,
      cssClass: 'profile-modal-css',
    });
    await modal.present();

    await modal.onDidDismiss().then(async data => {
      if (data.data) {
        if (!data.data.signature && data.data.changed) {
          await this.router.navigateByUrl(url, { skipLocationChange: true }).then(async () => {
            await this.router.navigate([url]);
          });
        }
      }
    });
  }

  async logout() {
    await this.userService.logout();
  }

  async gotoCornerstone(url?: string) {
    // if (navigator.userAgent.includes('Safari')) {
    // 	await this.sharedService.showToast(
    // 		this.translate.instant('panel.alert.navigator'),
    // 		2500,
    // 		'bottom',
    // 		'danger'
    // 	);
    // } else {
    let tabRes = '';
    let error = false;
    await lastValueFrom(this.cornerstoneService.gotoCornerstone(url ? url : '')).then(
      res => {
        tabRes = JSON.stringify(res);
      },
      error => {
        if (error.status === 200) {
          tabRes = error.error.text;
        } else {
          error = true;
          console.error(error);
        }
      },
    );
    if (!error) {
      setTimeout(() => {
        try {
          let newWindow = window.open('', '_blank');
          newWindow?.document.write(tabRes);
          newWindow?.document.close();

          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            alert(
              'Não foi possível abrir o link porque o seu navegador está bloqueando pop-ups. Por favor, ative os pop-ups para acessar o link.',
            );
          }
        } catch (e) {
          alert('Não foi possível abrir o link. Verifique as configurações de pop-up do seu navegador.');
        }
      });
    }
    // }
  }

  async checkPayments() {
    this.payslip = !(await this.checkRolesService.checkPayments());
  }

  async checkEmployeeUpdate() {
    this.showEmployeeUpdate = await this.checkRolesService.checkEmployeeUpdate();
  }

  async checkBenefitsStatement() {
    this.showBenefitsStatement = await this.checkRolesService.checkBenefitsStatementAccess();

    const benefitsApp = this.receiptsApps.find(app => app.routerParams === this.benefitsRouterParam);
    if (benefitsApp) {
      benefitsApp.hidden = !this.showBenefitsStatement;
    }
  }

  // Verifica acessos para exibir os links de redirecionamento
  async checkAccess() {
    const user = await this.checkRolesService.getUser();
    this.showIaAgent = await this.checkRolesService.checkAccessRole(this.targetLinks.iaAgent, user);
    this.showObservatory = await this.checkRolesService.checkAccessRole(this.targetLinks.observatory, user);
    this.showPortalUnipac = await this.checkRolesService.checkAccessRole(this.targetLinks.portalUnipac, user);
    this.showPSuggestion = await this.checkRolesService.checkAccessRole(this.targetLinks.programSuggestion, user);
    this.showServiceNow = await this.checkRolesService.checkAccessRole(this.targetLinks.serviceNow, user);
    this.showOnTime = await this.checkRolesService.checkAccessRole(this.targetLinks.onTime, user);
    this.showRecruitmentSelection = await this.checkRolesService.checkAccessRole(
      this.targetLinks.recruitmentSelection,
      user,
    );
    this.showTermManagement = await this.checkRolesService.checkAccessRole(this.targetLinks.termsManagement, user);
    this.showCornerstone = await this.checkRolesService.checkAccessRole(this.targetLinks.cornerstone, user);
  }

  async encryptAndRedirect(redirect: RedirectProperty) {
    let encryptedToken = await lastValueFrom(this.userService.encryptToken());

    if (encryptedToken) {
      // Padrão: abrir em nova aba, a menos que `blank` esteja explicitamente definido como falso
      const openInNewTab = redirect.blank !== false;

      if (openInNewTab) {
        window.open(`${redirect.link}?token=${encodeURIComponent(encryptedToken)}`, '_blank');
        return;
      }

      window.location.assign(`${redirect.link}?token=${encodeURIComponent(encryptedToken)}`);
    }
  }
}
